import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Breadcrumb,
  Grid,
  HStack,
  Icon,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import {
  MdChevronRight,
  MdPersonOutline,
} from "react-icons/md";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  useDashboardInstructor,
  usePatchDashboardInstructor,
  useSetDashboardInstructorAvatar,
} from "features/dashboard/instructores/hooks";
import { formatFechaYYYYMMDD } from "utils/formatters";
import { getAvatarHexColor, resolveAvatarUrl } from "utils/avatar";
import AppAvatar from "components/utils/AppAvatar";
import { DashboardInput, DashboardSelectContent, DashboardSelectItem, DashboardSelectTrigger } from "components/utils/FormControls";
import { FormDialog } from "components/utils/FormDialog";
import { storage } from "lib/firebase";
import InstructorDetailsTabs from "./InstructorDetailsTabs";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const INSTRUCTOR_ROLE_OPTIONS = ["Instructor", "Jefe de Nivel", "Subjefe de Nivel"];

function getFileExtension(file) {
  const base = (file?.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  const cleaned = base.replace(/[^a-z0-9]/g, "");
  if (cleaned) return cleaned;
  if (file?.type === "image/jpeg") return "jpg";
  if (file?.type === "image/png") return "png";
  if (file?.type === "image/webp") return "webp";
  return "jpg";
}

const toStr = (v) => (v == null ? "" : String(v));
const toNull = (v) => { const t = String(v ?? "").trim(); return t.length ? t : null; };

const InstructoresDetails = () => {
  const { id } = useParams();
  const avatarInputRef = useRef(null);

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [fNombre, setFNombre] = useState("");
  const [fFecha, setFFecha] = useState("");
  const [fTelefono, setFTelefono] = useState("");
  const [fGeneracion, setFGeneracion] = useState("");
  const [fRol, setFRol] = useState(INSTRUCTOR_ROLE_OPTIONS[0]);

  const q = useDashboardInstructor(id, true);
  const setAvatar = useSetDashboardInstructorAvatar();
  const patchM = usePatchDashboardInstructor();
  const roleCollection = useMemo(
    () => createListCollection({ items: INSTRUCTOR_ROLE_OPTIONS.map((role) => ({ label: role, value: role })) }),
    []
  );

  const instructor = q.data;
  const instructorId = instructor?.id ?? instructor?.instructorId ?? null;

  const nombre = useMemo(() => {
    const raw = instructor?.nombre ?? instructor?.instructorNombre;
    const clean = typeof raw === "string" ? raw.trim() : "";
    return clean || "Instructor";
  }, [instructor]);

  const roleText = instructor?.rol ?? instructor?.role ?? "Instructor";
  const avatarSrc = resolveAvatarUrl(instructor) || null;
  const avatarSeed = instructor?.id ?? instructor?.instructorId ?? nombre;
  const avatarColor = getAvatarHexColor(avatarSeed);

  const fechaNacimiento = useMemo(() => {
    const raw = instructor?.fechaNacimiento ?? instructor?.instructorFechaNacimiento;
    return raw ? formatFechaYYYYMMDD(raw) : null;
  }, [instructor]);

  const telefono = instructor?.telefono ?? instructor?.instructorTelefono ?? null;
  const generacion = instructor?.generacion ?? instructor?.instructorGeneracion ?? null;

  const saving = patchM.isPending;
  const formError = patchM.error;

  const onAvatarClick = () => { if (!isUploadingAvatar) avatarInputRef.current?.click(); };

  const onAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !id) return;
    if (!file.type?.startsWith("image/")) { setAvatarUploadError("Solo se permiten archivos de imagen."); return; }
    if (file.size > MAX_IMAGE_SIZE_BYTES) { setAvatarUploadError("El archivo supera el limite de 10MB."); return; }
    setAvatarUploadError("");
    setIsUploadingAvatar(true);
    try {
      const imageId = crypto.randomUUID();
      const ext = getFileExtension(file);
      const storagePath = `instructores/${id}/avatar/${imageId}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined);
      const downloadUrl = await getDownloadURL(storageRef);
      await setAvatar.mutateAsync({ instructorId: id, payload: { imageId, storagePath, downloadUrl, contentType: file.type || null, sizeBytes: file.size } });
    } catch (e) {
      setAvatarUploadError(String(e?.message || e || "No se pudo subir la foto."));
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const openEdit = () => {
    const roleRaw = toStr(instructor?.rol ?? instructor?.role);
    setFNombre(toStr(instructor?.nombre ?? instructor?.instructorNombre));
    setFFecha(toStr(instructor?.fechaNacimiento ?? instructor?.instructorFechaNacimiento));
    setFTelefono(toStr(instructor?.telefono ?? instructor?.instructorTelefono));
    setFGeneracion(toStr(instructor?.generacion ?? instructor?.instructorGeneracion));
    setFRol(INSTRUCTOR_ROLE_OPTIONS.includes(roleRaw) ? roleRaw : INSTRUCTOR_ROLE_OPTIONS[0]);
    setEditOpen(true);
  };

  const onSave = async () => {
    if (!instructorId) return;
    await patchM.mutateAsync({
      instructorId,
      patch: { nombre: toNull(fNombre), fechaNacimiento: toNull(fFecha), telefono: toNull(fTelefono), generacion: toNull(fGeneracion), rol: fRol },
    });
    setEditOpen(false);
  };

  const breadcrumb = (
    <Breadcrumb.Root pb="2">
      <Breadcrumb.List>
        <Breadcrumb.Item>
          <Breadcrumb.Link asChild>
            <Link to="/dashboard/club/instructores">
              <HStack><MdPersonOutline />Instructores</HStack>
            </Link>
          </Breadcrumb.Link>
        </Breadcrumb.Item>
        <Breadcrumb.Separator><MdChevronRight /></Breadcrumb.Separator>
        <Breadcrumb.Item>
          <Breadcrumb.CurrentLink>{nombre}</Breadcrumb.CurrentLink>
        </Breadcrumb.Item>
      </Breadcrumb.List>
    </Breadcrumb.Root>
  );

  if (q.isLoading) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" p="8" display="flex" justifyContent="center">
          <Spinner />
        </Box>
      </Box>
    );
  }

  if (q.isError) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" p="6">
          <Text color="dashboard.feedback.error" fontWeight="bold">Error cargando instructor</Text>
          <Text as="pre" whiteSpace="pre-wrap" color="dashboard.feedback.error" mt="2">
            {String(q.error?.message || q.error)}
          </Text>
        </Box>
      </Box>
    );
  }

  if (!instructor) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" p="6">
          <Text color="dashboard.feedback.error" fontWeight="bold">Instructor no encontrado en el periodo actual.</Text>
          <Text color="fg.muted" mt="2">Revisa que el periodo seleccionado en el dashboard sea el correcto.</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="5">
      {breadcrumb}

      <InstructorDetailsTabs
        instructorInfo={{ nombre, rol: roleText, fechaNacimiento, telefono, generacion }}
        onEditInfo={openEdit}
        sidebarHeader={(
          <Box position="relative">
            <Box h="2" bg={avatarColor} />
            <Box p={{ base: "4", md: "5" }}>
              <Stack gap="4" align="center" textAlign="center">
                <Box>
                  <Input ref={avatarInputRef} type="file" accept="image/*" onChange={onAvatarFileChange} display="none" />
                  <AppAvatar
                    name={nombre} src={avatarSrc} size={84} seed={avatarSeed} color={avatarColor}
                    cursor={isUploadingAvatar ? "progress" : "pointer"}
                    onClick={onAvatarClick}
                  />
                  {isUploadingAvatar && <Text fontSize="xs" color="dashboard.fg.muted" mt="1" textAlign="center">Subiendo...</Text>}
                  {avatarUploadError && <Text fontSize="xs" color="dashboard.feedback.error" mt="1" maxW="84px">{avatarUploadError}</Text>}
                </Box>

                <Box w="full" minW="0">
                  <Stack align="center" gap="2">
                    <Box minW="0">
                      <Text fontFamily="display" fontSize={{ base: "xl", md: "2xl" }} fontWeight="700" lineClamp={2} color="dashboard.fg.default" textAlign="center">
                        {nombre}
                      </Text>
                      <Box display="inline-flex" rounded="pill" px="2.5" py="0.5" bg={avatarColor} color="white" fontSize="xs" fontWeight="700" maxW="full" mt="2">
                        <HStack gap="1.5" minW="0" justify="center">
                          <Icon color="white" flexShrink={0}>
                            <MdPersonOutline />
                          </Icon>
                          <Text as="span" fontSize="xs" fontWeight="800" color="white" truncate>
                            {roleText}
                          </Text>
                        </HStack>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}
      />

      <FormDialog
        open={editOpen}
        onOpenChange={(e) => setEditOpen(e.open)}
        title="Editar Instructor"
        errorText={formError ? String(formError?.message || formError) : null}
        saving={saving}
        onCancel={() => setEditOpen(false)}
        onSave={onSave}
      >
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Nombre</Text>
          <DashboardInput value={fNombre} onChange={(e) => setFNombre(e.target.value)} placeholder="Ej. Jorge de la Garza" />
        </Box>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
          <Select.Root
            collection={roleCollection}
            size="sm"
            value={fRol ? [fRol] : []}
            onValueChange={(e) => setFRol(e.value?.[0] ?? INSTRUCTOR_ROLE_OPTIONS[0])}
            positioning={{ strategy: "fixed", hideWhenDetached: true }}
          >
            <Select.HiddenSelect />
            <Select.Label fontSize="sm" fontWeight="600" color="dashboard.fg.default">Rol</Select.Label>
            <Select.Control>
              <DashboardSelectTrigger>
                <Select.ValueText placeholder="Rol del instructor" />
              </DashboardSelectTrigger>
              <Select.IndicatorGroup>
                <Select.Indicator color="dashboard.accent.solid" />
              </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
              <DashboardSelectContent>
                {roleCollection.items.map((role) => (
                  <DashboardSelectItem item={role} key={role.value}>
                    {role.label}
                    <Select.ItemIndicator color="dashboard.accent.solid" />
                  </DashboardSelectItem>
                ))}
              </DashboardSelectContent>
            </Select.Positioner>
          </Select.Root>

          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Generacion</Text>
            <DashboardInput value={fGeneracion} onChange={(e) => setFGeneracion(e.target.value)} placeholder="Ej. 2024" />
          </Box>
        </Grid>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Telefono</Text>
            <DashboardInput type="tel" value={fTelefono} onChange={(e) => setFTelefono(e.target.value)} placeholder="Ej. 81 1234 5678" />
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Fecha de nacimiento</Text>
            <DashboardInput type="date" value={fFecha} onChange={(e) => setFFecha(e.target.value)} />
          </Box>
        </Grid>
      </FormDialog>
    </Box>
  );
};

export default InstructoresDetails;
